/**
 * Sync-Engine.
 *
 * Aufgaben:
 *  1. Outbox-Flush: jede Pending-Operation an den Server senden (Schichten +
 *     Kalender-Einträge).
 *  2. Server-Pull: aktuelle Schichten, Kalender-Einträge + Stammdaten holen und
 *     in IndexedDB cachen.
 *
 * Konflikte:
 *  - Schichten: bei 409 → Konflikt-Dialog (markConflict).
 *  - Kalender-Einträge: bei 409 → Server gewinnt (kein Dialog, überschreibt lokal).
 */

import { db } from './schema';
import * as outbox from './outbox';
import { ApiError } from '../lib/api';
import {
  shiftsApi,
  type ApiShift,
  type CreateShiftInput,
  type UpdateShiftInput,
} from '../lib/shifts';
import {
  calendarEntriesApi,
  type ApiCalendarEntry,
  type CreateCalendarEntryInput,
  type UpdateCalendarEntryInput,
} from '../lib/calendarEntries';
import { locationsApi } from '../lib/locations';
import { useAuth } from '../store/auth';

export interface SyncResult {
  sent: number;
  failed: number;
  retryLater: number;
  conflicted: number;
  pulled: boolean;
}

/**
 * Gesamtprüfung 11.09.2026: Event, wenn eine Offline-Änderung endgültig nicht
 * übernommen werden konnte (z. B. Doppelschicht, Tagesgehalt schon entnommen)
 * — SyncIndicator zeigt den Grund an, statt die Änderung stumm zu verwerfen.
 */
export const SYNC_FAILED_EVENT = 'shiftsync:sync-failed';

function reportSyncFailure(kind: string, message: string): void {
  console.warn(`[sync] ${kind} nicht übernommen:`, message);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SYNC_FAILED_EVENT, { detail: { kind, message } }));
  }
}

/** HTTP-Status, bei denen es sich lohnt, später erneut zu senden (kein inhaltlicher Fehler). */
const RETRYABLE_4XX = new Set([408, 425, 429]);

/** Lokale Kopie auf den Server-Stand zurücksetzen (bzw. entfernen, wenn es sie nicht mehr gibt). */
async function restoreFromServer(token: string, kind: string, entityId: string): Promise<void> {
  if (kind === 'shift.create') {
    await db.shifts.delete(entityId);
    return;
  }
  if (kind === 'calendarEntry.create') {
    await db.calendarEntries.delete(entityId);
    return;
  }
  if (kind.startsWith('shift')) {
    try {
      const fresh = await shiftsApi.get(token, entityId);
      await db.shifts.put({ ...fresh, pending: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) await db.shifts.delete(entityId);
      else {
        const ex = await db.shifts.get(entityId);
        if (ex) await db.shifts.put({ ...ex, pending: false });
      }
    }
    return;
  }
  // Kalender-Einträge: der nächste Pull überschreibt, sobald pending=false ist.
  const ex = await db.calendarEntries.get(entityId);
  if (ex) await db.calendarEntries.put({ ...ex, pending: false });
}

export async function runSync(token: string): Promise<SyncResult> {
  const result: SyncResult = { sent: 0, failed: 0, retryLater: 0, conflicted: 0, pulled: false };
  const { user: currentUser, activeCompanyId } = useAuth.getState();
  // Gesamtprüfung 11.09.2026: wurde währenddessen abgemeldet bzw. die Person
  // gewechselt, sofort aufhören — sonst schriebe ein noch laufender Sync die
  // Daten der vorigen Person zurück in den gerade geleerten lokalen Speicher.
  const stillSameSession = () => useAuth.getState().token === token;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const memberCompanyIds = new Set((currentUser?.memberships ?? []).map((m) => m.companyId));
  // Datensätze, deren frühere Änderung noch aussteht (Fehler/Konflikt/andere
  // Firma) — spätere Änderungen daran warten, sonst liefen sie ins Leere
  // (z. B. Ausstempeln, bevor das Einstempeln angekommen ist).
  const blockedEntities = new Set<string>();
  for (const c of await outbox.conflicts()) blockedEntities.add(c.entityId);

  // --- 1. Outbox abarbeiten ---
  const items = await outbox.pending();
  for (const item of items) {
    if (!stillSameSession()) return result;
    // Gesamtprüfung 11.09.2026: Änderungen einer ANDEREN Person (geteiltes
    // Gerät, erzwungene Abmeldung) nie mit dem Token der aktuellen Person
    // senden — sonst landete z. B. eine offline eingetragene Schicht im
    // falschen Konto. Die lokale Kopie verwerfen (der Pull holt den Stand,
    // den diese Person sehen darf).
    if (item.userId && currentUser && item.userId !== currentUser.id) {
      if (item.kind.startsWith('shift')) await db.shifts.delete(item.entityId);
      else await db.calendarEntries.delete(item.entityId);
      await outbox.remove(item.id!);
      result.failed++;
      continue;
    }
    // Firma, zu der die Person nicht mehr gehört → nie mehr sendbar.
    if (item.companyId && !isAdmin && currentUser && !memberCompanyIds.has(item.companyId)) {
      await restoreFromServer(token, item.kind, item.entityId).catch(() => undefined);
      await outbox.remove(item.id!);
      reportSyncFailure(item.kind, 'Änderung gehört zu einer Firma, in der du nicht mehr bist.');
      result.failed++;
      continue;
    }
    // Änderungen aus einer anderen Firma warten, bis diese wieder aktiv ist
    // (sonst 403 „Standort gehört nicht zur aktiven Firma“ → verloren).
    if ((item.companyId && item.companyId !== activeCompanyId) || blockedEntities.has(item.entityId)) {
      blockedEntities.add(item.entityId);
      result.retryLater++;
      continue;
    }
    try {
      switch (item.kind) {
        case 'shift.create': {
          const payload = item.payload as CreateShiftInput;
          const saved = await shiftsApi.create(token, payload);
          await db.shifts.delete(item.entityId);
          await db.shifts.put({ ...saved, pending: false });
          await outbox.remove(item.id!);
          if (saved.id !== item.entityId) {
            await outbox.rewriteEntityId(item.entityId, saved.id);
            for (const later of items) if (later.entityId === item.entityId) later.entityId = saved.id;
          }
          result.sent++;
          break;
        }
        case 'shift.update': {
          const payload = item.payload as UpdateShiftInput;
          const saved = await shiftsApi.update(token, item.entityId, payload, item.baseLastModified);
          await db.shifts.put({ ...saved, pending: false });
          await outbox.remove(item.id!);
          result.sent++;
          break;
        }
        case 'shift.delete': {
          await shiftsApi.remove(token, item.entityId, item.baseLastModified);
          await db.shifts.delete(item.entityId);
          await outbox.remove(item.id!);
          result.sent++;
          break;
        }
        case 'calendarEntry.create': {
          const payload = item.payload as CreateCalendarEntryInput;
          const saved = await calendarEntriesApi.create(token, payload);
          await db.calendarEntries.delete(item.entityId);
          await db.calendarEntries.put({ ...saved, pending: false });
          await outbox.remove(item.id!);
          if (saved.id !== item.entityId) {
            await outbox.rewriteEntityId(item.entityId, saved.id);
            for (const later of items) if (later.entityId === item.entityId) later.entityId = saved.id;
          }
          result.sent++;
          break;
        }
        case 'calendarEntry.update': {
          const payload = item.payload as UpdateCalendarEntryInput;
          const saved = await calendarEntriesApi.update(
            token,
            item.entityId,
            payload,
            item.baseLastModified,
          );
          await db.calendarEntries.put({ ...saved, pending: false });
          await outbox.remove(item.id!);
          result.sent++;
          break;
        }
        case 'calendarEntry.delete': {
          await calendarEntriesApi.remove(token, item.entityId, item.baseLastModified);
          await db.calendarEntries.delete(item.entityId);
          await outbox.remove(item.id!);
          result.sent++;
          break;
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        // 401 = abgemeldet; 403 PasswordChangeRequired (Kapitel 3.2, Screen 7) =
        // erst eigenes Passwort festlegen. Beides ist KEIN dauerhafter Fehler
        // der Änderung selbst — nichts verwerfen, später erneut senden.
        const passwordChangeRequired =
          err.status === 403 && (err.body as { error?: string } | null)?.error === 'PasswordChangeRequired';
        if (err.status === 401 || passwordChangeRequired) {
          result.retryLater = items.length - result.sent;
          return result;
        }
        // Kalender-Einträge: kein Konflikt-Dialog — Server gewinnt.
        if (err.status === 409 && item.kind.startsWith('calendarEntry')) {
          const body = err.body as { entry?: ApiCalendarEntry } | null;
          if (body?.entry) await db.calendarEntries.put({ ...body.entry, pending: false });
          else await restoreFromServer(token, item.kind, item.entityId);
          await outbox.remove(item.id!);
          result.conflicted++;
          if (!body?.entry) reportSyncFailure(item.kind, err.message);
          continue;
        }
        if (err.status === 409) {
          const body = err.body as { shift?: ApiShift } | null;
          const serverVersion = body?.shift;
          const localVersion = await db.shifts.get(item.entityId);
          if (serverVersion && localVersion) {
            await outbox.markConflict(item.id!, {
              serverVersion,
              localVersion,
              detectedAt: Date.now(),
            });
            blockedEntities.add(item.entityId);
            result.conflicted++;
          } else {
            // Gesamtprüfung 11.09.2026: 409 OHNE Server-Stand (z. B. genehmigte
            // Abwesenheit, Tagesgehalt schon entnommen) — lokale Kopie auf den
            // Server-Stand zurücksetzen, sonst hing sie für immer als „pending“.
            await restoreFromServer(token, item.kind, item.entityId);
            await outbox.remove(item.id!);
            reportSyncFailure(item.kind, err.message);
            result.failed++;
          }
          continue;
        }
        if (err.status >= 400 && err.status < 500 && !RETRYABLE_4XX.has(err.status)) {
          await restoreFromServer(token, item.kind, item.entityId);
          await outbox.remove(item.id!);
          reportSyncFailure(item.kind, err.message);
          result.failed++;
          continue;
        }
      }
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      await outbox.markFailed(item.id!, msg);
      blockedEntities.add(item.entityId);
      result.retryLater++;
    }
  }

  if (!stillSameSession()) return result;

  // --- 2. Server-Pull ---
  try {
    const [locations, shiftsLast90Days, entriesLast90Days] = await Promise.all([
      locationsApi.list(token, true),
      pullRecentShifts(token, 90),
      // Nicht-fatal: fehlt der Endpunkt (Backend noch nicht migriert), leere Liste.
      pullRecentCalendarEntries(token, 90).catch(() => [] as ApiCalendarEntry[]),
    ]);

    await db.transaction(
      'rw',
      db.locations,
      db.templates,
      db.shifts,
      db.calendarEntries,
      db.meta,
      async () => {
        if (!stillSameSession()) return;
        // Standorte komplett ersetzen
        await db.locations.clear();
        await db.locations.bulkPut(
          locations.map((l) => ({ ...l, templates: undefined } as never)),
        );
        await db.templates.clear();
        const templates = locations.flatMap((l) => l.templates ?? []);
        await db.templates.bulkPut(templates);

        // Schichten: nur server-bestätigte (pending=false) ersetzen.
        const localPending = await db.shifts.filter((s) => Boolean(s.pending)).toArray();
        const pendingIds = new Set(localPending.map((s) => s.id));
        const serverShifts = shiftsLast90Days.filter((s) => !pendingIds.has(s.id));
        const existing = await db.shifts.toArray();
        await db.shifts.bulkDelete(existing.filter((s) => !s.pending).map((s) => s.id));
        await db.shifts.bulkPut(serverShifts.map((s) => ({ ...s, pending: false })));

        // Kalender-Einträge: analog, pending unangetastet.
        const localPendingEntries = await db.calendarEntries
          .filter((e) => Boolean(e.pending))
          .toArray();
        const pendingEntryIds = new Set(localPendingEntries.map((e) => e.id));
        const serverEntries = entriesLast90Days.filter((e) => !pendingEntryIds.has(e.id));
        const existingEntries = await db.calendarEntries.toArray();
        await db.calendarEntries.bulkDelete(
          existingEntries.filter((e) => !e.pending).map((e) => e.id),
        );
        await db.calendarEntries.bulkPut(serverEntries.map((e) => ({ ...e, pending: false })));

        await db.meta.put({ key: 'lastSync', value: Date.now() });
      },
    );
    result.pulled = true;
  } catch (err) {
    console.warn('[sync] Pull fehlgeschlagen:', err);
  }

  return result;
}

function rangeIso(days: number): { fromIso: string; toIso: string } {
  const today = new Date();
  const past = new Date(today);
  past.setDate(past.getDate() - days);
  const future = new Date(today);
  future.setDate(future.getDate() + days);
  return { fromIso: past.toISOString().slice(0, 10), toIso: future.toISOString().slice(0, 10) };
}

async function pullRecentShifts(token: string, days: number) {
  const { fromIso, toIso } = rangeIso(days);
  return shiftsApi.list(token, { from: fromIso, to: toIso });
}

async function pullRecentCalendarEntries(token: string, days: number) {
  const { fromIso, toIso } = rangeIso(days);
  return calendarEntriesApi.list(token, { from: fromIso, to: toIso });
}

export async function getLastSync(): Promise<number | null> {
  const row = await db.meta.get('lastSync');
  return row && typeof row.value === 'number' ? row.value : null;
}
